import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Minus, Plus, ShoppingBag, ShoppingBasket, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type PackageKey = "pin" | "isim" | "normal" | "premium" | "iliski" | "test" | string;

type Package = {
  id: string;
  key: PackageKey;
  name: string;
  description: string;
  basePriceTry: number;
  discountedPriceTry?: number;
  active: boolean;
  imageUrl?: string;
};

type Product = {
  id: string;
  name: string;
  description: string;
  category: string;
  priceTry: number;
  discountedPriceTry?: number;
  active?: boolean;
  imageUrl?: string;
  imageUrls?: string[];
};

type ProductCartItem = {
  productId: string;
  qty: number;
};

type CatalogResponse = {
  ok: true;
  packages: Package[];
  products: Product[];
  settings: unknown;
};

type CartItem = {
  packageKey: PackageKey;
  qty: number;
};

const GOLD = "#d4af37";
const CART_STORAGE_KEY = "numeroloji_cart_v1";
const PRODUCT_CART_STORAGE_KEY = "numeroloji_product_cart_v1";
const VAT_RATE = 0.2;

function safeParseCart(raw: string | null): CartItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((it) => {
        const obj = it as any;
        if (obj?.digitalContent) return { digitalContent: obj.digitalContent } as any;
        const packageKey = typeof obj?.packageKey === "string" ? (obj.packageKey as PackageKey) : null;
        const qty = typeof obj?.qty === "number" ? obj.qty : Number(obj?.qty);
        if (!packageKey) return null;
        if (!Number.isFinite(qty) || qty <= 0) return null;
        return { packageKey, qty: Math.min(99, Math.trunc(qty)) } satisfies CartItem;
      })
      .filter(Boolean) as CartItem[];
  } catch {
    return [];
  }
}

function loadCart(): CartItem[] {
  return safeParseCart(globalThis?.localStorage?.getItem(CART_STORAGE_KEY) ?? null);
}

function saveCart(items: CartItem[]) {
  globalThis?.localStorage?.setItem(CART_STORAGE_KEY, JSON.stringify(items));
}

function safeParseProductCart(raw: string | null): ProductCartItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => {
        if (typeof entry === "string") {
          const productId = entry.trim();
          return productId ? ({ productId, qty: 1 } satisfies ProductCartItem) : null;
        }

        const obj = entry as any;
        const productId = String(obj?.productId ?? "").trim();
        const qty = Math.max(1, Math.min(99, Math.trunc(Number(obj?.qty ?? 1) || 1)));
        if (!productId) return null;
        return { productId, qty } satisfies ProductCartItem;
      })
      .filter(Boolean) as ProductCartItem[];
  } catch {
    return [];
  }
}

function loadProductCart(): ProductCartItem[] {
  return safeParseProductCart(globalThis?.localStorage?.getItem(PRODUCT_CART_STORAGE_KEY) ?? null);
}

function saveProductCart(items: ProductCartItem[]) {
  const normalized = items
    .map((item) => ({
      productId: String(item.productId ?? "").trim(),
      qty: Math.max(1, Math.min(99, Math.trunc(Number(item.qty ?? 1) || 1))),
    }))
    .filter((item) => item.productId);

  globalThis?.localStorage?.setItem(PRODUCT_CART_STORAGE_KEY, JSON.stringify(normalized));
}

async function fetchCatalog(): Promise<CatalogResponse> {
  const res = await fetch("/api/numeroloji/catalog");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Katalog alınamadı.");
  }
  return json as CatalogResponse;
}

function formatTry(amount: number) {
  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${Math.round(amount)} ₺`;
  }
}

function getPackageEffectivePrice(p: Package) {
  return typeof p.discountedPriceTry === "number" && p.discountedPriceTry > 0 ? p.discountedPriceTry : p.basePriceTry;
}

function getProductEffectivePrice(p: Product) {
  return typeof p.discountedPriceTry === "number" && p.discountedPriceTry > 0 ? p.discountedPriceTry : p.priceTry;
}

function getProductImage(p: Product) {
  const list = Array.from(new Set([...(p.imageUrls ?? []), p.imageUrl ?? ""].map((v) => String(v ?? "").trim()).filter(Boolean)));
  return list[0] ?? "";
}

function cardArt(key: PackageKey) {
  switch (key) {
    case "pin":
      return "[background:radial-gradient(circle_at_25%_25%,rgba(212,175,55,0.30)_0%,transparent_55%),linear-gradient(145deg,#151518,#0a0a0c)]";
    case "isim":
      return "[background:radial-gradient(circle_at_65%_30%,rgba(127,13,242,0.32)_0%,transparent_55%),linear-gradient(145deg,#151518,#0a0a0c)]";
    case "iliski":
      return "[background:radial-gradient(circle_at_50%_20%,rgba(212,175,55,0.22)_0%,transparent_60%),linear-gradient(145deg,#151518,#0a0a0c)]";
    case "premium":
      return "[background:radial-gradient(circle_at_40%_25%,rgba(212,175,55,0.25)_0%,transparent_60%),radial-gradient(circle_at_70%_65%,rgba(127,13,242,0.22)_0%,transparent_65%),linear-gradient(145deg,#151518,#0a0a0c)]";
    default:
      return "[background:radial-gradient(circle_at_40%_25%,rgba(127,13,242,0.22)_0%,transparent_60%),linear-gradient(145deg,#151518,#0a0a0c)]";
  }
}

export default function NumerolojiSepet() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const catalogQuery = useQuery({
    queryKey: ["numeroloji-catalog"],
    queryFn: fetchCatalog,
    staleTime: 60_000,
  });

  const packages = useMemo(
    () => (catalogQuery.data?.packages ?? []).filter((p) => p.active && p.key !== "test"),
    [catalogQuery.data],
  );

  const products = catalogQuery.data?.products ?? [];

  const [cart, setCart] = useState<CartItem[]>(() => loadCart());
  const [productCart, setProductCart] = useState<ProductCartItem[]>(() => loadProductCart());

  const digitalCartItem = useMemo(
    () => (cart.find((i: any) => i.digitalContent) as any)?.digitalContent ?? null,
    [cart],
  );
  const [coupon, setCoupon] = useState("");

  useEffect(() => {
    saveCart(cart);
  }, [cart]);

  useEffect(() => {
    saveProductCart(productCart);
  }, [productCart]);

  const cartItemsResolved = useMemo(() => {
    const byKey = new Map(packages.map((p) => [p.key, p] as const));
    return cart
      .map((it) => {
        const pkg = byKey.get(it.packageKey);
        if (!pkg) return null;
        return { item: it, pkg };
      })
      .filter(Boolean) as Array<{ item: CartItem; pkg: Package }>;
  }, [cart, packages]);

  const preselectedProductIds = useMemo(() => {
    const byList = (searchParams.get("productIds") ?? "")
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    const single = (searchParams.get("productId") ?? "").trim();
    return Array.from(new Set([...byList, ...(single ? [single] : [])]));
  }, [searchParams]);

  useEffect(() => {
    if (!preselectedProductIds.length) return;
    setProductCart((prev) => {
      const next = prev.map((item) => ({ ...item }));
      for (const id of preselectedProductIds) {
        const existing = next.find((item) => item.productId === id);
        if (!existing) next.push({ productId: id, qty: 1 });
      }
      return next;
    });
  }, [preselectedProductIds]);

  const productItemsResolved = useMemo(() => {
    const byId = new Map(products.map((p) => [p.id, p] as const));
    return productCart
      .map((item) => {
        const product = byId.get(item.productId);
        if (!product) return null;
        return {
          item,
          product,
        };
      })
      .filter(Boolean) as Array<{ item: ProductCartItem; product: Product }>;
  }, [productCart, products]);

  const subtotal = useMemo(() => {
    const packageSum = cartItemsResolved.reduce((sum, r) => sum + getPackageEffectivePrice(r.pkg) * r.item.qty, 0);
    const productSum = productItemsResolved.reduce((sum, row) => sum + getProductEffectivePrice(row.product) * row.item.qty, 0);
    const digitalSum = digitalCartItem ? Number(digitalCartItem.price ?? 0) : 0;
    return packageSum + productSum + digitalSum;
  }, [cartItemsResolved, digitalCartItem, productItemsResolved]);

  const vat = useMemo(() => {
    const gross = Math.max(0, Number(subtotal) || 0);
    const net = Math.round(gross / (1 + VAT_RATE));
    return Math.max(0, gross - net);
  }, [subtotal]);

  const total = subtotal;
  const subtotalNet = Math.max(0, total - vat);

  const cartCount = useMemo(() => {
    const packageCount = cart.reduce((acc, it) => acc + (Number.isFinite(it.qty) ? it.qty : 0), 0);
    const productCount = productCart.reduce((acc, it) => acc + (Number.isFinite(it.qty) ? it.qty : 0), 0);
    return packageCount + productCount;
  }, [cart, productCart]);

  const checkoutDisabled = !cartItemsResolved.length && !productItemsResolved.length && !digitalCartItem;

  function setQty(pkgKey: PackageKey, nextQty: number) {
    setCart((prev) =>
      prev
        .map((it) => (it.packageKey === pkgKey ? { ...it, qty: Math.max(1, Math.min(99, Math.trunc(nextQty))) } : it))
        .filter((it) => (it as any).digitalContent || it.qty > 0),
    );
  }

  function remove(pkgKey: PackageKey) {
    setCart((prev) => prev.filter((it) => (it as any).digitalContent || it.packageKey !== pkgKey));
  }

  function setProductQty(productId: string, nextQty: number) {
    setProductCart((prev) =>
      prev
        .map((item) =>
          item.productId === productId
            ? { ...item, qty: Math.max(1, Math.min(99, Math.trunc(Number(nextQty) || 1))) }
            : item,
        )
        .filter((item) => item.qty > 0),
    );
  }

  function removeProduct(productId: string) {
    setProductCart((prev) => prev.filter((item) => item.productId !== productId));
  }

  const recommendation = useMemo(() => {
    const inCart = new Set(cartItemsResolved.map((r) => r.pkg.key));
    return packages.find((p) => !inCart.has(p.key)) ?? null;
  }, [cartItemsResolved, packages]);

  return (
    <div className="min-h-dvh bg-[#050508] text-slate-200">
      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#050508]/80 backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4">
          <Link to="/numeroloji" className="flex items-center gap-3">
            <span className="grid h-8 w-8 place-items-center border border-[#d4af37] rotate-45">
              <span className="h-3 w-3 bg-[#d4af37]" />
            </span>
            <span className="font-display text-lg font-bold tracking-[0.16em] text-white">Numeroloji.app</span>
          </Link>

          <div className="flex items-center gap-6 sm:gap-8">
            <Link
              to="/numeroloji/magaza"
              className="font-display text-xs tracking-[0.22em] transition-colors hover:text-[rgba(212,175,55,0.95)] sm:text-sm"
            >
              MAĞAZA
            </Link>
            <Link
              to="/numeroloji#kasalar"
              className="hidden font-display text-xs tracking-[0.22em] transition-colors hover:text-[rgba(212,175,55,0.95)] sm:block sm:text-sm"
            >
              ANALİZLER
            </Link>

            <Link to="/numeroloji/sepet" className="relative">
              <ShoppingBag className="h-5 w-5 text-slate-200" />
              {cartCount ? (
                <span
                  className="absolute -right-2 -top-2 rounded-full px-1.5 py-0.5 text-[10px] font-bold text-black"
                  style={{ backgroundColor: GOLD }}
                  aria-label={`Sepette ${cartCount} ürün`}
                >
                  {cartCount}
                </span>
              ) : null}
            </Link>
          </div>
        </div>
      </nav>

      <main className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6">
        <div className="mb-10">
          <h1 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">Mistik Sepetiniz</h1>
          <p className="mt-2 text-sm text-slate-400">Kaderinizin anahtarları burada sizi bekliyor.</p>
        </div>

        {catalogQuery.isLoading ? (
          <div className="rounded-xl border p-6 text-sm text-slate-300" style={{ borderColor: "rgba(212,175,55,0.12)" }}>
            Yükleniyor...
          </div>
        ) : null}

        {catalogQuery.isError ? (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
            {(catalogQuery.error as Error).message}
          </div>
        ) : null}

        {!catalogQuery.isLoading && !catalogQuery.isError ? (
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <section className="lg:col-span-8">
              <div
                className="overflow-hidden rounded-xl border"
                style={{ borderColor: "rgba(212,175,55,0.18)", backgroundColor: "rgba(26,18,37,0.35)" }}
              >
                <div className="w-full overflow-auto">
                  <table className="w-full border-collapse text-left text-sm">
                    <thead>
                      <tr
                        className="border-b"
                        style={{ borderColor: "rgba(212,175,55,0.14)", backgroundColor: "rgba(26,18,37,0.55)" }}
                      >
                        <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
                          Hizmet / Ürün
                        </th>
                        <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
                          Adet
                        </th>
                        <th className="px-6 py-4 text-right text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
                          Fiyat
                        </th>
                        <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500" />
                      </tr>
                    </thead>

                    <tbody className="divide-y" style={{ borderColor: "rgba(212,175,55,0.14)" }}>
                      {cartItemsResolved.map(({ item, pkg }) => {
                        const line = getPackageEffectivePrice(pkg) * item.qty;
                        return (
                          <tr key={pkg.key}>
                            <td className="px-6 py-6">
                              <div className="flex items-center gap-4">
                                <div
                                  className={cn("relative h-16 w-16 overflow-hidden rounded-lg border", cardArt(pkg.key))}
                                  style={{ borderColor: "rgba(212,175,55,0.18)" }}
                                >
                                  {pkg.imageUrl?.trim() ? (
                                    <img
                                      src={pkg.imageUrl}
                                      alt={pkg.name || "Paket görseli"}
                                      className="absolute inset-0 h-full w-full object-cover"
                                      loading="lazy"
                                    />
                                  ) : null}
                                  <div className="absolute inset-0 bg-black/20" />
                                </div>
                                <div className="min-w-0">
                                  <Link
                                    to={`/numeroloji/magaza/${encodeURIComponent(String(pkg.key))}`}
                                    className="block truncate font-semibold text-slate-100 hover:opacity-90"
                                  >
                                    {pkg.name}
                                  </Link>
                                </div>
                              </div>
                            </td>

                            <td className="px-6 py-6">
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  className="grid h-8 w-8 place-items-center rounded border bg-black/20 text-slate-300 transition-colors hover:text-white"
                                  style={{ borderColor: "rgba(212,175,55,0.18)" }}
                                  onClick={() => setQty(pkg.key, item.qty - 1)}
                                  aria-label="Azalt"
                                >
                                  <Minus className="h-4 w-4" />
                                </button>
                                <span className="w-6 text-center font-semibold text-slate-100">{item.qty}</span>
                                <button
                                  type="button"
                                  className="grid h-8 w-8 place-items-center rounded border bg-black/20 text-slate-300 transition-colors hover:text-white"
                                  style={{ borderColor: "rgba(212,175,55,0.18)" }}
                                  onClick={() => setQty(pkg.key, item.qty + 1)}
                                  aria-label="Arttır"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              </div>
                            </td>

                            <td className="px-6 py-6 text-right">
                              <div className="space-y-1">
                                {typeof pkg.discountedPriceTry === "number" && pkg.discountedPriceTry > 0 && pkg.discountedPriceTry < pkg.basePriceTry ? (
                                  <div className="text-xs text-right opacity-50 line-through">{formatTry(pkg.basePriceTry * item.qty)}</div>
                                ) : null}
                                <div className="font-semibold" style={{ color: GOLD }}>
                                  {formatTry(line)}
                                </div>
                              </div>
                            </td>

                            <td className="px-6 py-6 text-right">
                              <div className="flex flex-col items-end gap-3">
                                <button
                                  type="button"
                                  className="text-slate-500 transition-colors hover:text-red-400"
                                  onClick={() => remove(pkg.key)}
                                  aria-label="Sil"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}

                      {productItemsResolved.map(({ item, product }) => {
                        const image = getProductImage(product);
                        const line = getProductEffectivePrice(product) * item.qty;
                        return (
                          <tr key={`product-${product.id}`}>
                            <td className="px-6 py-6">
                              <div className="flex items-center gap-4">
                                <div
                                  className="relative h-16 w-16 overflow-hidden rounded-lg border"
                                  style={{ borderColor: "rgba(212,175,55,0.18)", backgroundColor: "rgba(212,175,55,0.06)" }}
                                >
                                  {image ? <img src={image} alt={product.name} className="absolute inset-0 h-full w-full object-cover" loading="lazy" /> : null}
                                  <div className="absolute inset-0 bg-black/20" />
                                </div>
                                <div className="min-w-0">
                                  <div className="truncate font-semibold text-slate-100">{product.name}</div>
                                  <div className="mt-1 text-xs text-slate-500">Mağaza ürünü</div>
                                </div>
                              </div>
                            </td>

                            <td className="px-6 py-6">
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  className="grid h-8 w-8 place-items-center rounded border bg-black/20 text-slate-300 transition-colors hover:text-white"
                                  style={{ borderColor: "rgba(212,175,55,0.18)" }}
                                  onClick={() => setProductQty(product.id, item.qty - 1)}
                                  aria-label="Azalt"
                                >
                                  <Minus className="h-4 w-4" />
                                </button>
                                <span className="w-6 text-center font-semibold text-slate-100">{item.qty}</span>
                                <button
                                  type="button"
                                  className="grid h-8 w-8 place-items-center rounded border bg-black/20 text-slate-300 transition-colors hover:text-white"
                                  style={{ borderColor: "rgba(212,175,55,0.18)" }}
                                  onClick={() => setProductQty(product.id, item.qty + 1)}
                                  aria-label="Arttır"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              </div>
                            </td>

                            <td className="px-6 py-6 text-right">
                              <div className="font-semibold" style={{ color: GOLD }}>
                                {formatTry(line)}
                              </div>
                            </td>

                            <td className="px-6 py-6 text-right">
                              <button
                                type="button"
                                className="text-slate-500 transition-colors hover:text-red-400"
                                onClick={() => removeProduct(product.id)}
                                aria-label="Sil"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}

                      {digitalCartItem && (
                        <tr>
                          <td className="px-6 py-6">
                            <div className="flex items-center gap-4">
                              <div
                                className="grid h-16 w-16 shrink-0 place-items-center rounded-lg border"
                                style={{ borderColor: "rgba(212,175,55,0.18)", background: "linear-gradient(135deg,#1e1e2e,#0e0e14)" }}
                              >
                                <span className="text-xs font-bold uppercase tracking-widest opacity-60" style={{ color: GOLD }}>
                                  {digitalCartItem.contentType === "meditation" ? "MED" : digitalCartItem.contentType === "breath" ? "NEF" : "FRK"}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <div className="truncate font-semibold text-slate-100">{digitalCartItem.title}</div>
                                <div className="mt-1 text-[11px] text-slate-500">Soul Circles uygulamasında erişilebilir</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-6 text-slate-400 text-sm">—</td>
                          <td className="px-6 py-6 text-right font-semibold" style={{ color: GOLD }}>
                            {formatTry(digitalCartItem.price)}
                          </td>
                          <td className="px-6 py-6 text-right">
                            <button
                              type="button"
                              className="text-slate-500 transition-colors hover:text-red-400"
                              onClick={() => setCart((prev) => prev.filter((i: any) => !i.digitalContent))}
                              aria-label="Sil"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      )}

                      {!cartItemsResolved.length && !productItemsResolved.length && !digitalCartItem ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-10 text-center text-sm text-slate-300">
                            Sepetin boş. Mağazaya gidip bir analiz seç.
                            <div className="mt-4">
                              <Button
                                asChild
                                className="rounded-xl px-6 font-display text-xs uppercase tracking-[0.22em] text-black"
                                style={{ backgroundColor: GOLD }}
                              >
                                <Link to="/numeroloji/magaza">Mağazaya git</Link>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                {[
                  { title: "Güvenli Ödeme", desc: "Iyzico ile güvenli ödeme" },
                  { title: "Teslimat", desc: "PDF rapor e-posta ile" },
                  { title: "Destek", desc: "Sorun olursa bize yaz" },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="flex items-center gap-3 rounded-xl border p-4"
                    style={{
                      borderColor: "rgba(212,175,55,0.14)",
                      backgroundColor: "rgba(26,18,37,0.20)",
                    }}
                  >
                    <span className="grid h-9 w-9 place-items-center rounded-lg border" style={{ borderColor: "rgba(212,175,55,0.18)" }}>
                      <ShoppingBasket className="h-4 w-4" style={{ color: GOLD }} />
                    </span>
                    <div className="text-xs">
                      <div className="font-semibold text-slate-200">{item.title}</div>
                      <div className="text-slate-500">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {recommendation ? (
                <div className="mt-12">
                  <h3 className="mb-6 flex items-center gap-2 font-display text-lg tracking-tight text-white">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: GOLD }} />
                    Bunu da Beğenebilirsiniz
                  </h3>

                  <div
                    className="flex flex-col items-center gap-6 rounded-2xl border p-5 md:flex-row"
                    style={{
                      borderColor: "rgba(212,175,55,0.14)",
                      backgroundColor: "rgba(26,18,37,0.20)",
                    }}
                  >
                    <div
                      className={cn(
                        "relative aspect-[3/4] w-full max-w-[140px] overflow-hidden rounded-lg border",
                        cardArt(recommendation.key),
                      )}
                      style={{ borderColor: "rgba(212,175,55,0.18)" }}
                      aria-hidden
                    >
                      {recommendation.imageUrl?.trim() ? (
                        <img
                          src={recommendation.imageUrl}
                          alt={recommendation.name || "Paket görseli"}
                          className="absolute inset-0 h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : null}
                      <div className="absolute inset-0 bg-black/15" />
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <span
                        className="rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em]"
                        style={{ borderColor: "rgba(212,175,55,0.25)", color: GOLD, backgroundColor: "rgba(212,175,55,0.10)" }}
                      >
                        Analiz Tavsiyesi
                      </span>
                      <div className="mt-3 text-lg font-semibold text-slate-100">{recommendation.name}</div>
                      <div className="mt-1 text-sm text-slate-400">{recommendation.description}</div>
                      <div className="mt-4 flex flex-wrap items-center justify-center gap-4 md:justify-start">
                        <div className="text-xl font-semibold text-slate-100">{formatTry(recommendation.basePriceTry)}</div>
                        <Button
                          className="h-10 rounded-xl px-4 font-display text-xs font-bold uppercase tracking-[0.22em] text-black"
                          style={{ backgroundColor: GOLD }}
                          onClick={() => {
                            setCart((prev) => {
                              if (prev.some((it) => it.packageKey === recommendation.key)) return prev;
                              return [...prev, { packageKey: recommendation.key, qty: 1 }];
                            });
                          }}
                        >
                          Sepete Ekle
                        </Button>
                        <Button
                          variant="outline"
                          className="h-10 rounded-xl border-[rgba(212,175,55,0.35)] bg-transparent px-4 font-display text-xs uppercase tracking-[0.22em] text-slate-200 hover:bg-[rgba(212,175,55,0.10)]"
                          asChild
                        >
                          <Link to={`/numeroloji/magaza/${encodeURIComponent(String(recommendation.key))}`}>Detaylar</Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </section>

            <aside className="lg:col-span-4">
              <div
                className="rounded-2xl border p-8 lg:sticky lg:top-28"
                style={{
                  borderColor: "rgba(212,175,55,0.12)",
                  backgroundColor: "rgba(28, 26, 16, 0.40)",
                  backdropFilter: "blur(12px)",
                }}
              >
                <div className="text-xl font-semibold text-slate-100">Sipariş Özeti</div>

                <div className="mt-6 space-y-4 text-sm">
                  <div className="flex items-center justify-between text-slate-400">
                    <span>Ara Toplam</span>
                    <span>{formatTry(subtotalNet)}</span>
                  </div>
                  <div className="flex items-center justify-between text-slate-400">
                    <span>KDV (%20)</span>
                    <span>{formatTry(vat)}</span>
                  </div>
                  <div className="border-t pt-4" style={{ borderColor: "rgba(212,175,55,0.14)" }}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-100">Genel Toplam</span>
                      <span className="text-2xl font-semibold" style={{ color: GOLD }}>
                        {formatTry(total)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <div className="ml-1 text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">Kupon Kodu</div>
                  <div className="mt-2 flex gap-2">
                    <Input
                      value={coupon}
                      onChange={(e) => setCoupon(e.target.value)}
                      placeholder="KODUNUZ"
                      className="h-11 flex-1 border-[rgba(212,175,55,0.14)] bg-black/20 text-slate-100 placeholder:text-slate-600"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 rounded-xl border-[rgba(212,175,55,0.35)] bg-transparent px-4 font-semibold"
                      style={{ color: GOLD }}
                      onClick={() => {
                        // coupon system later
                      }}
                    >
                      Uygula
                    </Button>
                  </div>
                </div>

                <Button
                  className="mt-6 h-12 w-full rounded-xl font-display text-xs font-bold uppercase tracking-[0.22em] text-black"
                  style={{ backgroundColor: GOLD }}
                  disabled={checkoutDisabled}
                  onClick={() => {
                    if (!cartItemsResolved.length && !productItemsResolved.length && !digitalCartItem) return;
                    const params = new URLSearchParams();
                    if (productCart.length) params.set("productIds", productCart.map((item) => item.productId).join(","));
                    navigate(params.toString() ? `/numeroloji/satin-al?${params.toString()}` : `/numeroloji/satin-al`);
                  }}
                >
                  Ödeme adımına geç
                </Button>

                {productCart.length ? (
                  <div className="mt-3 text-center text-xs text-slate-500">
                    Panelden eklenen ürün, ödeme adımında sipariş özetine otomatik eklenir.
                  </div>
                ) : null}

                <div className="mt-6 flex items-center justify-center gap-3 opacity-40">
                  <div className="h-6 w-10 rounded bg-white/10" aria-hidden />
                  <div className="h-6 w-10 rounded bg-white/10" aria-hidden />
                  <div className="h-6 w-10 rounded bg-white/10" aria-hidden />
                </div>
              </div>
            </aside>
          </div>
        ) : null}
      </main>

      <footer className="mt-20 border-t py-12" style={{ borderColor: "rgba(212,175,55,0.10)" }}>
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-8 px-4">
          <div className="h-24 w-px bg-gradient-to-b from-[rgba(212,175,55,0.95)] to-transparent" />
          <div className="text-center">
            <div className="font-display text-3xl tracking-widest" style={{ color: GOLD }}>
              Numeroloji.app
            </div>
            <p className="mt-3 text-xs uppercase tracking-[0.22em] opacity-40">
              Evrensel Sayılar Rehberi © {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
